import NotificationsNoneIcon from '@material-symbols/svg-300/outlined/notifications.svg'
import NotificationsActiveIcon from '@material-symbols/svg-300/outlined/notifications_active.svg'
import classnames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useMarkNotificationsRead from '../../state/mutations/useMarkNotificationsRead'
import useNotificationCount from '../../state/queries/useNotificationCount'
import useNotfications from '../../state/queries/useNotifications'
import useInvalidate from '../../state/useInvalidate'
import useRoutes from '../../state/useRoutes'
import Button from '../basics/Button/Button'
import Popup from '../basics/Popup/Popup'
import notificationComponents from '../notificationComponents/notificationComponents'

import styles from './NotificationBell.module.scss'

export default React.memo(function NotificationBell() {
  const routes = useRoutes()
  const invalidate = useInvalidate()
  const { t } = useTranslation()

  // Count
  const { notificationCount } = useNotificationCount()

  // Notifications
  const { notifications } = useNotfications()

  const unreadNotifications = useMemo(() => notifications.filter(({ readAt }) => !readAt).slice(0, 3), [notifications])

  // Popup
  const [open, setOpen] = useState(false)

  const doOpen = useCallback(() => {
    setOpen(true)
  }, [setOpen])

  const doClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  // Mark all read
  const markNotificationsRead = useMarkNotificationsRead()
  const handleMarkAllRead = useCallback(
    () => markNotificationsRead.mutate({ notificationUuids: unreadNotifications.map(({ uuid }) => uuid) }),
    [unreadNotifications]
  )

  return (
    <div className={classnames(styles.NotificationBell, { [styles.hasUnread]: notificationCount > 0 })}>
      <Button onClick={doOpen} small filled>
        {notificationCount === 0 ? <NotificationsNoneIcon /> : <NotificationsActiveIcon />}
      </Button>
      <span className={styles.notificationCount}>{notificationCount}</span>
      <div>
        <Popup open={open} onRequestClose={doClose} menu className={styles.popup}>
          {unreadNotifications.map((notification) => {
            const { Icon, Message, Action } =
              notificationComponents[notification.type] || notificationComponents.Unknown

            return (
              <div
                key={notification.uuid}
                className={classnames(styles.notification, { skeleton: notification.isDummy })}
              >
                <div className={styles.iconAndDate}>
                  <Icon {...({ notification: notification as any } as any)} />
                  <span>{t('format.RELATIVEDATE', { date: notification.createdAt })}</span>
                </div>
                <div className={styles.messageAndAction}>
                  <div>
                    <Message {...({ notification: notification as any } as any)} />
                  </div>
                  <div>
                    <Action {...({ notification: notification as any } as any)} />
                  </div>
                </div>
              </div>
            )
          })}
          {!unreadNotifications.length && (
            <p className={styles.empty}>{t('notifications.no unread notifications found')}</p>
          )}
          <div className={styles.actions}>
            <Button component={Link} to={routes.notifications.url({})} onClick={() => invalidate.notifications()} small>
              {t('notifications.cta view all')}
            </Button>
            <Button small onClick={handleMarkAllRead}>
              {t('notifications.cta mark all read')}
            </Button>
          </div>
        </Popup>
      </div>
    </div>
  )
})
